/* eslint-disable class-methods-use-this */
/**
 * Copyright (C) 2020 Labs64 GmbH
 *
 * This source code is licensed under the European Union Public License, version 1.2
 * located in the LICENSE file
 */
// utils
import domTemplate from "../../utils/domTemplate";

// templates
import beaconTpl from "./templates/beacon.html";
import beaconWithLabel from "./templates/beaconWithLabel.html";
import infoIcon from "./assets/icons/info.svg";
import helpIcon from "./assets/icons/helpIcon.svg";
import warningIcon from "./assets/icons/warning.svg";
import beacon from "./assets/icons/beacon.svg";
import { TRIGGER_MODE } from "../../utils/constants";

export default class Beacons {
  constructor(beacons, options = {}) {
    this.beacons = [];

    this.options = {};

    // observers
    this.observers = {};

    // Store pending beacons for elements not yet in DOM
    this.pendingBeacons = [];
    
    // Interval ID for periodic element search
    this.elementSearchIntervalId = null;
    
    // Default search interval in milliseconds (5 seconds)
    this.elementSearchInterval = 5000;

    if (typeof ResizeObserver !== "undefined") {
      this.observers.elementResizeObserver = new ResizeObserver(() =>
        this.refresh()
      );
    }

    if (typeof MutationObserver !== "undefined") {
      this.observers.domMutationObserver = new MutationObserver(() =>
        this.handleDomMutations()
      );
    }

    this.cache = new Map();
    this.elements = new Map();

    this.setOptions(options);
    this.setBeacons(beacons);

    this.init();
  }

  /**
   * Called after construction, this hook allows you to add some extra setup
   * logic without having to override the constructor.
   */
  init() {}

  /**
   * Default options
   * @return {Object}
   */
  static getDefaultOptions() {
    return {
      position: "center",
      boundary: "inner",
      elementSearchInterval: 5000, // Search for missing elements every 5 seconds
    };
  }

  static getFixedClass() {
    return "gc-beacon-fixed";
  }

  static getBeaconDataPrefix() {
    return "data-beacon";
  }

  /**
   * Check if el or his parent has fixed position
   * @param el
   * @return {boolean}
   */
  static isFixed(el) {
    const { parentNode } = el;

    if (!parentNode || parentNode.nodeName === "HTML") {
      return false;
    }

    const elStyle = getComputedStyle(el);

    if (elStyle.getPropertyValue("position") === "fixed") {
      return true;
    }

    return this.isFixed(parentNode);
  }

  /**
   * Set beacons options
   * @param options
   * @return {this}
   */
  setOptions(options) {
    this.options = { ...this.constructor.getDefaultOptions(), ...options };
    this.elementSearchInterval = this.options.elementSearchInterval;
    return this;
  }

  setBeacons(beacons) {
    // cleanup for previous beacons
    this.removeAll();

    this.beacons =
      !beacons ||
      typeof beacons === "string" ||
      (Array.isArray(beacons) && beacons.every((v) => typeof v === "string"))
        ? this.getDataBeacons(beacons)
        : this.getJsBeacons(beacons);

    // Reset pending beacons
    this.pendingBeacons = [];

    if (this.beacons.length) {
      this.beacons.forEach((beacon) => {
        const { element } = beacon;

        if (!element) {
          return;
        }

        const el = this.getEl(element);

        if (!el) {
          // Element not found, add to pending beacons for later search
          this.pendingBeacons.push(beacon);
          return;
        }

        this.createAndAttachBeacon(beacon, el);
      });

      this.addOnWindowResizeListener();
      this.addOnScrollListener();
      this.observeDomMutations();
      
      // Try to resolve pending beacons immediately (DOM might already contain targets)
      this.searchForPendingElements();

      // Start periodic search for pending beacons
      this.startElementSearch();
    }

    return this;
  }

  /**
   * Create and attach a beacon element to the DOM
   * @param {Object} beacon - The beacon configuration
   * @param {HTMLElement} el - The target element
   * @return {this}
   */
  createAndAttachBeacon(beacon, el) {
    const beaconEl = this.createBeaconEl(beacon);
    beaconEl.hidden = true;
    if (this.constructor.isFixed(el)) {
      beaconEl.classList.add(this.constructor.getFixedClass());
    }
    if (beacon.class === "info-tooltip") {
      beaconEl.classList.remove("gc-beacon");
    }
    if (beacon.class === "help-tooltip") {
      beaconEl.classList.remove("gc-beacon");
    }

    const parentEl = document.body;

    parentEl.append(beaconEl);
    this.elements.set(beacon, beaconEl);
    this.setBeaconPosition(el, beaconEl, beacon);
   if (beacon._forceShow) {
      beaconEl.hidden = false;
      delete beacon._forceShow;
    } else if (this.isCanShowBeacon(beacon)) {
      beaconEl.hidden = false;
    }

    // fire observers
    this.observeResizing(el);

    return this;
  }

  /**
   * Start periodic search for pending beacon elements
   * @return {this}
   */
  startElementSearch() {
    // Clear any existing interval
    this.stopElementSearch();

    // Only start if there are pending beacons
    if (this.pendingBeacons.length > 0) {
      this.elementSearchIntervalId = setInterval(() => {
        this.searchForPendingElements();
      }, this.elementSearchInterval);
    }

    return this;
  }

  /**
   * Stop periodic search for pending beacon elements
   * @return {this}
   */
  stopElementSearch() {
    if (this.elementSearchIntervalId) {
      clearInterval(this.elementSearchIntervalId);
      this.elementSearchIntervalId = null;
    }

    return this;
  }

  /**
   * Search for pending elements and create beacons when found
   * @return {this}
   */
  searchForPendingElements() {
    if (this.pendingBeacons.length === 0) {
      this.stopElementSearch();
      return this;
    }

    const stillPending = [];

    this.pendingBeacons.forEach((beacon) => {
      const { element } = beacon;
      const el = this.getEl(element);

      if (el && document.body.contains(el)) {
        // Element found, create the beacon
        this.createAndAttachBeacon(beacon, el);
      } else {
        // Element still not found, keep in pending
        stillPending.push(beacon);
      }
    });

    this.pendingBeacons = stillPending;

    // Stop interval if no more pending beacons
    if (this.pendingBeacons.length === 0) {
      this.stopElementSearch();
    }

    return this;
  }

  getBeacons() {
    return this.beacons;
  }

  getBeacon(id, def) {
    const [beacon] =
      id && typeof id === "object"
        ? [id]
        : this.beacons.filter((v) => v.id === id);
    return beacon || def;
  }

  getDataBeacons(ids) {
    const beaconsIds =
      typeof ids === "string" ? ids.split(",").map((v) => v.trim()) : ids;

    let beaconsSelector = [`[${this.constructor.getBeaconDataPrefix()}]`];

    if (beaconsIds) {
      beaconsSelector = [];

      beaconsIds.forEach((id) => {
        beaconsSelector.push(
          `[${this.constructor.getBeaconDataPrefix()}*='${id}']`
        );
      });
    }

    const beaconsEl = Array.from(
      document.querySelectorAll(beaconsSelector.join(","))
    );
    const dataGlobalRegExp = new RegExp(
      `^${this.constructor.getBeaconDataPrefix()}-([^-]+)$`
    );

    const beacons = [];

    beaconsEl.forEach((el) => {
      const { value: beaconsIdsAttrValue } =
        el.attributes[this.constructor.getBeaconDataPrefix()];

      if (!beaconsIdsAttrValue) {
        return;
      }

      const elBeaconsIds = beaconsIdsAttrValue.split(",");

      elBeaconsIds.forEach((id) => {
        if (beaconsIds && !beaconsIds.includes(id)) {
          return;
        }

        const globalBeaconAttrs = {};
        const beaconAttrs = {};

        const dataBeaconRegExp = new RegExp(
          `^${this.constructor.getBeaconDataPrefix()}-${id}-([^-]+)$`
        );

        // parse attributes
        for (let j = 0; j < el.attributes.length; j++) {
          const { name: attrName, value: attrValue } = el.attributes[j];

          const isGlobalAttr = dataGlobalRegExp.test(attrName);
          const isBeaconAttr = dataBeaconRegExp.test(attrName);

          if (isGlobalAttr) {
            const [, shortAttrName] = attrName.match(dataGlobalRegExp);

            globalBeaconAttrs[shortAttrName] = attrValue;
          } else if (isBeaconAttr) {
            const [, shortAttrName] = attrName.match(dataBeaconRegExp);

            beaconAttrs[shortAttrName] = attrValue;
          }
        }

        const beacon = {
          id,
          position: this.options.position,
          ...globalBeaconAttrs,
          ...beaconAttrs,
          element: el,
        };

        // change onclick event
        const onClick = beacon.onclick || beacon.onClick;

        if (onClick) {
          delete beacon.onclick;

          beacon.onClick = (e) => {
            // eslint-disable-next-line no-eval
            const onClickCode = eval(onClick);

            if (typeof onClickCode === "function") {
              onClickCode.call(e, beacon);
            }
          };
        }

        beacons.push(beacon);
      });
    });

    return beacons;
  }

  getJsBeacons(beacons) {
    // cast to array
    const array = !Array.isArray(beacons) ? [beacons] : beacons;

    return array.map((v, i) => ({ ...v, id: v.id || i }));
  }

  getBeaconTpl(data) {
    if (data?.triggerMode === TRIGGER_MODE.label || data?.triggerMode === TRIGGER_MODE.noIcon) {
      return beaconWithLabel;
    }
    return beaconTpl;
  }

  createBeaconEl(beacon) {
    const data = { ...beacon };
    data.onClick = (e) => {
      e.stopPropagation();
      if (beacon.onClick) {
        beacon.onClick.call(this, e, beacon);
      }
    };

    return domTemplate(this.getBeaconTpl(data), { beacon: data });
  }

  getEl(selector) {
    return selector instanceof HTMLElement
      ? selector
      : document.querySelector(selector);
  }

  iconType(type) {
    switch (type) {
      case "help":
        return `url(https://i.ibb.co/1tTctTQP/helpIcon.png)`;
      case "info":
        return `url(https://i.ibb.co/g20HNY0/infoIcon.png)`;
      case "warning":
        return `url(https://i.ibb.co/XrbmwHFp/warning-Icon.png)`;
      default:
        return `url(https://i.ibb.co/ccYMGHZR/beacon-Icon.png)`;
    }
  }

  setBeaconPosition(el, beaconEl, options = {}) {
    let { position, boundary } = options;
    const { triggerIcon, triggerLabel, triggerMode } = options;
    position = position || this.options.position;
    boundary = boundary || this.options.boundary;
    boundary = boundary === "inner" ? "inner" : "outer";

    const elRect = el.getBoundingClientRect();
    const isFixed = beaconEl.classList.contains(this.constructor.getFixedClass());
    
    let elLeft, elTop;
    if (isFixed) {
      elLeft = elRect.left;
      elTop = elRect.top;
    } else {
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      elLeft = elRect.left + scrollLeft;
      elTop = elRect.top + scrollTop;
    }
    
    const elWidth = elRect.width;
    const elHeight = elRect.height;
    
    const { style: beaconStyle } = beaconEl;
    let { width: beaconWidth, height: beaconHeight } =
      getComputedStyle(beaconEl);

    beaconWidth = parseInt(beaconWidth, 10);
    beaconHeight = parseInt(beaconHeight, 10);

    beaconEl.removeAttribute("data-beacon-position");
    beaconEl.removeAttribute("data-beacon-boundary");

    beaconEl.setAttribute("data-beacon-position", position);
    beaconEl.setAttribute("data-beacon-boundary", boundary);

    if(triggerMode === TRIGGER_MODE.icon) {
      beaconStyle.backgroundImage = this.iconType(triggerIcon?.type);
      beaconStyle.opacity = triggerIcon?.opacity ?? 1;
      beaconStyle.backgroundColor = triggerIcon?.color ?? "white";
      if (!triggerIcon?.isAnimated) {
        beaconEl.classList.add("gc-beacon-no-anim");
      }
    }

    if(triggerMode === TRIGGER_MODE.label || triggerMode === TRIGGER_MODE.noIcon) {
      beaconStyle.opacity = triggerLabel?.opacity ?? 1;
      beaconStyle.backgroundColor = triggerLabel?.background ?? "#fff";
      beaconStyle.color = triggerLabel?.color ?? "#000";
      const text = triggerMode === TRIGGER_MODE.label ? triggerLabel?.text ?? "" : "";
      beaconEl.setAttribute("data-beacon-label", text);
    }

    switch (position) {
      case "top-left": {
        if (boundary === "inner") {
          beaconStyle.left = `${elLeft}px`;
          beaconStyle.top = `${elTop}px`;
        } else {
          beaconStyle.left = `${elLeft - beaconWidth}px`;
          beaconStyle.top = `${elTop - beaconHeight}px`;
        }

        break;
      }

      case "top": {
        beaconStyle.left = `${elLeft + (elWidth - beaconWidth) / 2}px`;
        beaconStyle.top =
          boundary === "inner" ? `${elTop}px` : `${elTop - beaconHeight}px`;

        break;
      }

      case "top-right": {
        if (boundary === "inner") {
          beaconStyle.left = `${elWidth + elLeft - beaconWidth}px`;
          beaconStyle.top = `${elTop}px`;
        } else {
          beaconStyle.left = `${elWidth + elLeft}px`;
          beaconStyle.top = `${elTop - beaconHeight}px`;
        }

        break;
      }

      case "left": {
        beaconStyle.left =
          boundary === "inner" ? `${elLeft}px` : `${elLeft - beaconWidth}px`;
        beaconStyle.top = `${elTop + (elHeight - beaconHeight) / 2}px`;

        break;
      }

      case "right": {
        beaconStyle.left =
          boundary === "inner"
            ? `${elLeft + elWidth - beaconWidth}px`
            : `${elLeft + elWidth}px`;
        beaconStyle.top = `${elTop + (elHeight - beaconHeight) / 2}px`;

        break;
      }

      case "bottom-left": {
        if (boundary === "inner") {
          beaconStyle.left = `${elLeft}px`;
          beaconStyle.top = `${elTop + elHeight - beaconHeight}px`;
        } else {
          beaconStyle.left = `${elLeft - beaconWidth}px`;
          beaconStyle.top = `${elTop + elHeight}px`;
        }

        break;
      }

      case "bottom": {
        beaconStyle.left = `${elLeft + (elWidth - beaconWidth) / 2}px`;
        beaconStyle.top =
          boundary === "inner"
            ? `${elTop + elHeight - beaconHeight}px`
            : `${elTop + elHeight}px`;

        break;
      }

      case "bottom-right": {
        if (boundary === "inner") {
          beaconStyle.left = `${elWidth + elLeft - beaconWidth}px`;
          beaconStyle.top = `${elTop + elHeight - beaconHeight}px`;
        } else {
          beaconStyle.left = `${elWidth + elLeft}px`;
          beaconStyle.top = `${elTop + elHeight}px`;
        }

        break;
      }

      case "center":
      default: {
        beaconEl.setAttribute("data-beacon-position", "center");
        beaconStyle.left = `${elLeft + (elWidth - beaconWidth) / 2}px`;
        beaconStyle.top = `${elTop + (elHeight - beaconHeight) / 2}px`;

        break;
      }
    }

    return this;
  }

  isCanShowBeacon({ canShow }) {
    if (canShow !== undefined) {
      if (!canShow || (typeof canShow === "function" && canShow() === false)) {
        return false;
      }
    }

    return true;
  }

  showAll(force = false) {
    this.beacons.forEach((beacon) => {
      this.show(beacon, force);
    });

    return this;
  }

  show(id, force = false) {
    const beacon = this.getBeacon(id);

    if (beacon) {
      const beaconEl = this.elements.get(beacon);

      if (beaconEl) {
        if (force || this.isCanShowBeacon(beacon)) {
          beaconEl.hidden = false;
        }
      } else if (force) {
        beacon._forceShow = true;
      }
    }

    return this;
  }

  hideAll() {
    this.beacons.forEach((beacon) => {
      this.hide(beacon);
    });

    return this;
  }

  hide(id) {
    const beacon = this.getBeacon(id);

    if (beacon) {
      const el = this.elements.get(beacon);

      if (el) {
        el.hidden = true;
      }
    }

    return this;
  }

  removeAll() {
    this.beacons.forEach((beacon) => {
      this.remove(beacon);
    });

    this.beacons = [];
    this.pendingBeacons = [];
    this.unobserveResizeAllElements();
    this.removeOnWindowResizeListener();
    this.removeOnScrollListener();
    this.unobserveDomMutations();
    this.stopElementSearch();

    return this;
  }

  remove(id) {
    const beacon = this.getBeacon(id);

    const beaconEl = this.elements.get(beacon);

    if (beaconEl) {
      beaconEl.parentNode.removeChild(beaconEl);
      const beaconIndex = this.beacons.indexOf(beacon);

      if (beaconIndex !== -1) {
        this.beacons.splice(this.beacons.indexOf(beacon), 1);
      }

      this.elements.delete(beacon);

      const el = this.getEl(beacon.element);

      if (el) {
        this.unobserveResizing(el);
      }
    }

    if (!this.beacons.length) {
      this.removeOnWindowResizeListener();
      this.removeOnScrollListener();
    }

    return this;
  }

  refresh() {
    this.beacons.forEach((beacon) => {
      const { element } = beacon;

      if (!element) {
        return;
      }

      const el = this.getEl(element);
      const beaconEl = this.elements.get(beacon);

      if (el && beaconEl) {
        // Check if element is still in the DOM
        if (document.body.contains(el)) {
          this.setBeaconPosition(el, beaconEl, beacon);
          if (this.isCanShowBeacon(beacon)) {
            beaconEl.hidden = false;
          }
        } else {
          // Element is no longer in the DOM, hide the beacon
          beaconEl.hidden = true;
        }
      }
    });

    return this;
  }

  /**
   * Add window resize event listener
   * @return {this}
   */
  addOnWindowResizeListener() {
    this.cache.set("onWindowResizeListener", this.getOnWindowResizeListener());
    window.addEventListener(
      "resize",
      this.cache.get("onWindowResizeListener"),
      true
    );

    return this;
  }

  /**
   * Return on window resize event listener function
   * @returns {function}
   */
  getOnWindowResizeListener() {
    return () => this.refresh();
  }

  /**
   * Remove window resize event listener
   * @return {this}
   */
  removeOnWindowResizeListener() {
    if (this.cache.has("onWindowResizeListener")) {
      window.removeEventListener(
        "resize",
        this.cache.get("onWindowResizeListener"),
        true
      );
      this.cache.delete("onWindowResizeListener");
    }

    return this;
  }

  /**
   * Add scroll event listener
   * @return {this}
   */
  addOnScrollListener() {
    this.cache.set("onScrollListener", this.getOnScrollListener());
    window.addEventListener(
      "scroll",
      this.cache.get("onScrollListener"),
      true
    );

    return this;
  }

  /**
   * Return on scroll event listener function
   * @returns {function}
   */
  getOnScrollListener() {
    return () => this.refresh();
  }

  /**
   * Remove scroll event listener
   * @return {this}
   */
  removeOnScrollListener() {
    if (this.cache.has("onScrollListener")) {
      window.removeEventListener(
        "scroll",
        this.cache.get("onScrollListener"),
        true
      );
      this.cache.delete("onScrollListener");
    }

    return this;
  }

  /**
   * Observe resize step element
   * @return {this}
   */
  observeResizing(el, options = { box: "border-box" }) {
    const { elementResizeObserver: observer } = this.observers;

    if (observer) {
      observer.observe(el, options);
    }

    return this;
  }

  /**
   * Unobserve resize step element
   * @param el
   * @return {this}
   */
  unobserveResizing(el) {
    const { elementResizeObserver: observer } = this.observers;

    if (observer) {
      observer.unobserve(el);
    }

    return this;
  }

  /**
   * Unobserve all resize steps elements
   * @return {this}
   */
  unobserveResizeAllElements() {
    const { elementResizeObserver: observer } = this.observers;

    if (observer) {
      observer.disconnect();
    }

    return this;
  }

  /**
   * Handle DOM mutations to detect when target elements are removed or added
   * @return {this}
   */
  handleDomMutations() {
    // Check existing beacons
    this.beacons.forEach((beacon) => {
      const { element } = beacon;

      if (!element) {
        return;
      }

      const el = this.getEl(element);
      const beaconEl = this.elements.get(beacon);

      if (beaconEl) {
        if (!el || !document.body.contains(el)) {
         beaconEl.hidden = true;
        } else if (this.isCanShowBeacon(beacon)) {
          beaconEl.hidden = false;
          this.setBeaconPosition(el, beaconEl, beacon);
        }
      }
    });

    // Also check for pending elements that might now be in the DOM
    this.searchForPendingElements();

    return this;
  }

  /**
   * Observe DOM mutations to detect when target elements are removed
   * @return {this}
   */
  observeDomMutations() {
    const { domMutationObserver: observer } = this.observers;

    if (observer) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    return this;
  }

  /**
   * Stop observing DOM mutations
   * @return {this}
   */
  unobserveDomMutations() {
    const { domMutationObserver: observer } = this.observers;

    if (observer) {
      observer.disconnect();
    }

    return this;
  }
}
